import React, {useEffect, useState} from "react";
import {Spinner} from "components/common/Spinner";
import {AnalysisScore, MathEx} from "predictagram-lib";
import {Form, Formik} from "formik";
import ReactDatePicker from "react-datepicker";
import {adminApiServiceCommon, adminSignalAlertApiService} from "../../../../services/AdminApiService";
import {StockHelper} from "../../../../_utils/stock.helper";
import {DropDown} from "../../../common/form/DropDown";
import {ExportToCsv} from "export-to-csv-fix-source-map";
import {AnalysisDailyProfitSharesOutput} from "predictagram-lib/dist/interface/report/analysis-daily-profit-shares";
import {SymbolIdsDropdown} from "../../../common/SymbolIdsDropdown";

type FilterOptions = {startTime:number, endTime:number, signalAlertIds:number[]};
const Filter: React.FunctionComponent<{
  initialValues?: Partial<FilterOptions>,
  onClick: (search:any)=>void,
}> = ({ initialValues, onClick }) => {

  const stockDay = StockHelper.findTradingDay(new Date(), -1, false);
  const todayStart = stockDay.openAt();
  const todayEnd = stockDay.closeAt();

  const [alerts, setAlerts] = useState<any>([]);

  const defVals: FilterOptions = {
    startTime: todayStart,
    endTime: todayEnd,
    signalAlertIds: [],
  };
  const initVals: FilterOptions = Object.assign(defVals, initialValues) as FilterOptions;

  useEffect(() => {
    (async () => {
      const signalAlerts = await adminSignalAlertApiService.getAll(true);
      const mp = new Map<number, string>();
      for (const s of signalAlerts) {
        mp.set(s.id, `${s.id} - ${s.name}`);
      }
      setAlerts(mp);

    })();
  }, []);


  return (
    <div>
      <Formik initialValues={initVals} onSubmit={onClick}>
        {({ values, touched, errors, setFieldValue }) => {
          return <Form>
            <div className="d-flex justify-content-start align-items-center gap-3 my-3">
              <DropDown enumMap={alerts}
                  errors={errors}
                  touched={touched}
                  label="Signal" name={'signalAlertIds'}
                  multiple={true}
              />

              <SymbolIdsDropdown  errors={errors} touched={touched} name="stockSymbolId"/>
              <div className="d-flex flex-column">
                <span className="fw-bold">Start Date</span>
                <ReactDatePicker
                  dateFormat="MM/dd/yyyy"
                  onChange={(date: Date) => {
                    setFieldValue("startTime", date.getTime() / 1000);
                  }}
                  selected={new Date(values.startTime * 1000)}
                />
              </div>

              <div className="d-flex flex-column">
                <span className="fw-bold">End Date</span>
                <ReactDatePicker
                  dateFormat="MM/dd/yyyy"
                  onChange={(date: Date) => {
                    setFieldValue("endTime", date.getTime() / 1000);
                  }}
                  selected={new Date(values.endTime * 1000)}
                />
              </div>
              <div className="align-self-end">
                <button type="submit" className="btn btn-primary p-2">Generate Report</button>
              </div>
            </div>

          </Form>
        }}

      </Formik>
    </div>
  )
}


export const AdminReportAlertCombined: React.FunctionComponent = () => {


  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');

  const generateReportWrap = async(searchOptions:FilterOptions) => {
    try {
      await generateReport(searchOptions);
    } catch(e:any) {
      console.error(e);
      setStatus(e.toString());
    }
  }

  const generateReport = async(searchOptions:FilterOptions) => {
    setStatus('');
    setIsLoading(true);

    const data:AnalysisDailyProfitSharesOutput[] = await adminApiServiceCommon.analysisStrategyProfitCombined(searchOptions);

    type RepAlertSignal = {
      signalAlertId: number,
      overallPct: number,
      posDirProfitPct: number,
      negDirProfitPct: number,
      trades: number,
      tradesProfitable: number,
      overallAmount: number,
    }

    type RepItem = {
      date: string;
      algoAccount: number,
      closePrice: number,
      totalTrades: number,
      tradeWinRate: number,
      totalProfit: number,
      profitPerTrade: number,
      algoAccountValue: number,
      benchmarkValue: number,

      alerts: RepAlertSignal[],
    };
    const rep: RepItem[] = [];
    const toFixed = (v: number, pos: number = 6) => pos == null ? pos : v.toFixed(pos);
    let maxTrades = 0;
    for (const d of data) {
      const repItem: RepItem = {
        date: d.date,
        algoAccount: 0,
        closePrice: d.stockClosePrice,
        profitPerTrade: 0,
        totalProfit: 0,
        totalTrades: 0,
        tradeWinRate: 0,
        algoAccountValue: 0,
        benchmarkValue: 0,
        alerts: [],
      };
      rep.push(repItem);
      let profitableTrades = 0;
      for (const a of d.alertResults) {
        const stats = a.result as AnalysisScore;
        const scores = stats.score;
        const amount = scores.profitAmountSum
                       + scores.lossAmountSum
                       + scores.atCloseAmountSum
                       + scores.atExitSignalAmountSum
                       + scores.atExitStepSizeAmountSum;
        const totalTrades = stats.trades.total;
        const profTrades = stats.trades.profitable;
        const repItemAlert:RepAlertSignal = {
          signalAlertId: a.signalAlertId,
          overallPct: toFixed(scores.overall * 100, 4) as any,
          posDirProfitPct: toFixed(scores.positiveDirection * 100, 4) as any,
          negDirProfitPct: toFixed(scores.negativeDirection * 100, 4) as any,
          trades: totalTrades,
          tradesProfitable: profTrades,
          overallAmount: toFixed(amount,3) as any,
        };
        repItem.totalTrades+=totalTrades;
        profitableTrades+=profTrades;
        repItem.totalProfit+=MathEx.round(amount,2);
        repItem.alerts.push(repItemAlert);
      }
      maxTrades = Math.max(maxTrades, repItem.totalTrades);
      repItem.profitPerTrade = repItem.totalTrades ? MathEx.round(repItem.totalProfit/repItem.totalTrades, 3) : 0;
      repItem.tradeWinRate = repItem.totalTrades ? MathEx.round(profitableTrades / repItem.totalTrades, 4): 0;
    }

    for (let i=0;i<rep.length;i++) {
      const curr = rep[i];
      if (i===0) {
        curr.benchmarkValue = MathEx.round(maxTrades*curr.closePrice,2);
        curr.algoAccountValue = MathEx.round(curr.benchmarkValue + curr.totalProfit,2);
        curr.algoAccount = curr.closePrice + curr.profitPerTrade;
      } else {
        const prev = rep[i-1];
        curr.benchmarkValue = MathEx.round(maxTrades*curr.closePrice,2);
        curr.algoAccountValue = MathEx.round(prev.algoAccountValue + curr.totalProfit,2);
        curr.algoAccount = prev.algoAccount + curr.profitPerTrade;
      }
    }

  const repFinal:any[] = [];

    for (const r of rep) {
      const alerts = r.alerts;
      delete (r as any).alerts;
      const rec={};
      Object.assign(rec, r);
      for (const a of alerts) {
        const signalId = a.signalAlertId;
        // add space
        Object.assign(rec, {
          [`signalNumber${signalId}`]: ''
        });
        delete (a as any).signalAlertId;
        for (const k of Object.keys(a)) {
          Object.assign(rec, {
            [`${k}${signalId}`]: (a as any)[k],
          });
        }
      }
      repFinal.push(rec);

    }

    // const dateStr = (time:number)=>DateHelper.dateTimeFormatUs(time).substring(0, 10);
    const filename = `report`;
    // if (rep.length === 0) {
    //   setStatus('no data found for specified range');
    // } else {
      const csv = new ExportToCsv({ useKeysAsHeaders: true, quoteStrings: '', filename});
      csv.generateCsv(repFinal);
    // }

    setIsLoading(false);
  }

  return (
    <>
    <div>
      <div className="d-flex justify-content-center align-items-end gap-3">
      <Filter onClick={generateReportWrap}/>
      </div>
    </div>

    {isLoading && <Spinner />}

    {status && <div>{status}</div>}
    </>
  );
};


